import type * as React from "react";

import { cn } from "../lib/cn";

/** Status tones a Dot can carry. `idle` is the achromatic default. */
export type DotTone = "ok" | "warn" | "crit" | "info" | "brand" | "idle";

const TONE_BG: Record<DotTone, string> = {
  ok: "bg-success",
  warn: "bg-warning",
  crit: "bg-destructive",
  info: "bg-info",
  brand: "bg-primary",
  idle: "bg-muted-foreground/72",
};

/**
 * A 7px status dot - the non-colour half of a signal, so a state is never
 * carried by hue alone (FR-X-6).
 *
 * `live` pulses an expanding ring to mark something still running. The ring is
 * a box-shadow keyframe rather than a scaled pseudo-element so it cannot shift
 * layout in a table cell, and it inherits `prefers-reduced-motion` from the
 * shared stylesheet rather than animating regardless.
 */
function Dot({
  tone = "idle",
  live,
  className,
  ...props
}: React.ComponentProps<"span"> & { tone?: DotTone; live?: boolean }) {
  return (
    <span
      data-slot="dot"
      data-tone={tone}
      data-live={live ? "" : undefined}
      className={cn(
        "size-[7px] shrink-0 rounded-full",
        // `live` is a running signal, not a severity: it reads ok unless the
        // thing it marks is already failing, which is the one tone that wins.
        live && tone !== "crit" ? "bg-success" : TONE_BG[tone],
        live && "animate-dot-pulse",
        className,
      )}
      {...props}
    />
  );
}

export { Dot };
